import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import LayerSelectionMenu from './LayerSelectionMenu';
import { showMapClickInfo, hideMapClickInfo } from './clickToIdentifySlice';
import { FEATURE_SERVICE, MAP_SERVICE } from './constants';
import { queryFeatureLayer, identifyMapService } from './layerHelpers';
import type {
  MapPoint,
  MapClickEvent,
  FeatureData,
  MapClickInfo,
  LayerSelectionResult,
  LayerSelectionMenuData,
  RootState,
  ClickToIdentifyGLProps,
  GISService,
} from './types';

const formatMapClickInfo = (
  event: { mapPoint: MapPoint },
  result: Partial<FeatureData>,
): MapClickInfo => {
  return {
    latitude: event.mapPoint.latitude,
    longitude: event.mapPoint.longitude,
    featureData: {
      layerName: result.layerName || '',
      layerId: result.layerId || '',
      attributes: result.attributes || {},
      fields: Object.keys(result.attributes || {}).map(key => ({
        name: key,
        alias: key,
      })),
      parentFeatureLayerID: result.layerId || result.layerName || '',
      isLoading: result.isLoading || false,
    },
  };
};

/**
 * Identify and analyze points on the Esri map using both feature layers and map image layers
 */
type LayerWithSublayers = { allSublayers?: { items?: unknown[] } };

const ClickToIdentifyGL = forwardRef<HTMLElement, ClickToIdentifyGLProps>(
  (
    {
      gisServiceLayers,
      gisBasemapSublayers,
      mapInstance,
      identifyTolerance = 3,
      queryBufferMeters = 3,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const mapRef = typeof ref === 'object' && ref?.current ? ref.current : null;
    const view = mapInstance?.view;

    const activeGISServices = useSelector(
      (state: RootState) => state.gisServices?.activeGISServices || [],
    );
    const mapClickInfo = useSelector(
      (state: RootState) => state.clickToIdentify?.mapClickInfo,
    );
    const basemapService = useSelector(
      (state: RootState) =>
        state.gisServices?.basemapService || ({} as GISService),
    );

    const activeBasemapServiceSublayers = useSelector(
      (state: RootState) =>
        state.gisServices?.activeBasemapServiceSublayers || [],
    );

    const isBasemapServiceOn = useMemo(() => {
      return activeBasemapServiceSublayers.length > 0;
    }, [activeBasemapServiceSublayers]);

    const activeServiceLayers = useMemo(() => {
      const activeLayers = [];

      // Add regular GIS service layers
      if (gisServiceLayers && activeGISServices.length) {
        const activeGISLayers = gisServiceLayers.filter(serviceLayer =>
          activeGISServices.find(
            activeService =>
              activeService.id === serviceLayer?.service?.id &&
              activeService?.attributes?.is_click_to_identify_service,
          ),
        );
        activeLayers.push(...activeGISLayers);
      }

      // Add active basemap sublayers
      if (gisBasemapSublayers && activeBasemapServiceSublayers.length) {
        const activeBasemapLayers = gisBasemapSublayers.filter(sublayer =>
          activeBasemapServiceSublayers.find(
            activeBasemapService =>
              activeBasemapService.id === sublayer?.service?.id,
          ),
        );
        activeLayers.push(...activeBasemapLayers);
      }

      return activeLayers;
    }, [
      gisServiceLayers,
      activeGISServices,
      gisBasemapSublayers,
      activeBasemapServiceSublayers,
    ]);

    const [layerSelectionMenu, setLayerSelectionMenu] =
      useState<LayerSelectionMenuData | null>(null);

    const { featureLayers, mapImageLayers } = useMemo(() => {
      if (!activeServiceLayers.length) {
        return { featureLayers: [], mapImageLayers: [] };
      }

      const featureLayers = activeServiceLayers.filter(layer => {
        const isBasemapSublayer = gisBasemapSublayers?.some(
          basemapLayer => basemapLayer?.service?.id === layer?.service?.id,
        );

        if (isBasemapSublayer) {
          return (
            layer?.service?.attributes?.gis_service_category === FEATURE_SERVICE
          );
        }

        return (
          layer?.service?.attributes?.is_click_to_identify_service &&
          layer?.service?.attributes?.gis_service_category === FEATURE_SERVICE
        );
      });

      const mapImageServiceLayers = activeServiceLayers.filter(layer => {
        const isBasemapSublayer = gisBasemapSublayers?.some(
          basemapLayer => basemapLayer?.service?.id === layer?.service?.id,
        );

        if (isBasemapSublayer) {
          return (
            layer?.service?.attributes?.gis_service_category === MAP_SERVICE
          );
        }

        return (
          layer?.service?.attributes?.is_click_to_identify_service &&
          layer?.service?.attributes?.gis_service_category === MAP_SERVICE
        );
      });

      const mapImageLayers = mapImageServiceLayers.map(serviceLayer => ({
        layer: serviceLayer.layer,
        service: serviceLayer.service,
      }));

      return { featureLayers, mapImageLayers };
    }, [activeServiceLayers, gisBasemapSublayers]);

    const hasActiveLayers =
      featureLayers.length > 0 ||
      mapImageLayers.length > 0 ||
      isBasemapServiceOn;

    const handlePopupClose = useCallback(() => {
      dispatch(hideMapClickInfo());
      setLayerSelectionMenu(null);
    }, [dispatch]);

    const handleLayerSelect = useCallback(
      async (selectedResult: LayerSelectionResult) => {
        setLayerSelectionMenu(null);
        // Immediately show popover with loading state
        dispatch(
          showMapClickInfo(
            formatMapClickInfo(
              {
                mapPoint: {
                  latitude: selectedResult.latitude,
                  longitude: selectedResult.longitude,
                },
              },
              {
                layerName: selectedResult.data?.display_name,
                layerId: String(selectedResult?.data?.id || ''),
                attributes: {},
                isLoading: true, // Add loading flag
              },
            ),
          ),
        );

        const attributesBySource: Record<string, Record<string, unknown>[]> =
          {};

        // Handle basemap service separately
        if (
          isBasemapServiceOn &&
          selectedResult.data.id === basemapService?.id
        ) {
          try {
            if (gisBasemapSublayers.length > 0) {
              // Query each feature layer in gisBasemapSublayers
              for (const basemapLayer of gisBasemapSublayers) {
                const layer = basemapLayer.layer;
                const serviceCategory =
                  basemapLayer.service?.attributes?.gis_service_category;

                if (serviceCategory === FEATURE_SERVICE) {
                  // Use query for feature services
                  const queryResult = await queryFeatureLayer({
                    layer,
                    longitude: selectedResult?.mapPoint?.longitude || 0,
                    latitude: selectedResult?.mapPoint?.latitude || 0,
                    fallbackLayerName:
                      (basemapLayer.service?.sublayerName as string) ||
                      'Basemap Feature Layer',
                    bufferMeters: queryBufferMeters,
                  });

                  if (queryResult) {
                    if (!attributesBySource[queryResult.sourceTitle]) {
                      attributesBySource[queryResult.sourceTitle] = [];
                    }
                    attributesBySource[queryResult.sourceTitle].push(
                      queryResult.attributes,
                    );
                  }
                } else {
                  // Use identify for map services
                  const url =
                    `${basemapLayer.service?.gis_service_url}/${basemapLayer.service.layerIndex}` ||
                    `${basemapService.gis_service_url}/${basemapLayer.service.layerIndex}`;
                  const token =
                    basemapLayer.service?.token || basemapService?.token;

                  const identifyResult = await identifyMapService({
                    url,
                    longitude: selectedResult.mapPoint.longitude,
                    latitude: selectedResult.mapPoint.latitude,
                    view,
                    token,
                    tolerance: identifyTolerance,
                    fallbackLayerName:
                      (basemapLayer.service?.sublayerName as string) ||
                      'Basemap Map Service',
                  });

                  if (identifyResult) {
                    if (!attributesBySource[identifyResult.sourceTitle]) {
                      attributesBySource[identifyResult.sourceTitle] = [];
                    }
                    attributesBySource[identifyResult.sourceTitle].push(
                      identifyResult.attributes,
                    );
                  }
                }
              }
            } else {
              // Fallback to original identify method if no basemapLayers
              const identifyResult = await identifyMapService({
                url: basemapService.gis_service_url,
                longitude: selectedResult.mapPoint.longitude,
                latitude: selectedResult.mapPoint.latitude,
                view,
                token: basemapService?.token,
                tolerance: identifyTolerance,
                fallbackLayerName: 'Basemap',
              });

              if (identifyResult) {
                if (!attributesBySource[identifyResult.sourceTitle]) {
                  attributesBySource[identifyResult.sourceTitle] = [];
                }
                attributesBySource[identifyResult.sourceTitle].push(
                  identifyResult.attributes,
                );
              }
            }
          } catch (error) {
            console.error('Basemap query/identify failed:', error);
          }
        } else {
          // Handle supplemental service layers
          for (const serviceLayer of activeServiceLayers) {
            let layer = serviceLayer.layer as unknown;

            // if the url ends with a numeric id, then we would query the first sublayer
            // otherwise, we need to query the layer normally
            // this is one of the side effects of trying to keep dynamic label while having controls of sublayers visibility
            // currently we are only support this for 1 client (Vermont - Responder Boundaries Supplemental layer)
            // should revisit this when geocomm has turn on the capability for other layers as well
            const rawUrl = String(serviceLayer.service?.gis_service_url || '');
            const trimmed = rawUrl.replace(/\/+$/, '');
            const lastSegment = trimmed.split('/').pop() || '';
            const endsWithNumericId = /^\d+$/.test(lastSegment);
            if (!endsWithNumericId) {
              const items = (layer as LayerWithSublayers)?.allSublayers?.items;
              if (Array.isArray(items) && items.length > 0) {
                layer = items[0] as unknown;
              }
            }

            if (
              selectedResult.data.id === serviceLayer.service.id &&
              selectedResult.layerType === FEATURE_SERVICE &&
              serviceLayer.service.attributes.gis_service_category ===
                FEATURE_SERVICE
            ) {
              const queryResult = await queryFeatureLayer({
                layer,
                longitude: selectedResult?.mapPoint?.longitude || 0,
                latitude: selectedResult?.mapPoint?.latitude || 0,
                fallbackLayerName: 'Unknown Layer',
                bufferMeters: queryBufferMeters,
              });

              if (queryResult) {
                if (!attributesBySource[queryResult.sourceTitle]) {
                  attributesBySource[queryResult.sourceTitle] = [];
                }
                attributesBySource[queryResult.sourceTitle].push(
                  queryResult.attributes,
                );
              }
            } else if (
              selectedResult.data.id === serviceLayer.service.id &&
              selectedResult.layerType === MAP_SERVICE &&
              serviceLayer.service.attributes.gis_service_category ===
                MAP_SERVICE
            ) {
              const identifyResult = await identifyMapService({
                url: serviceLayer.service.gis_service_url,
                longitude: selectedResult.mapPoint.longitude,
                latitude: selectedResult.mapPoint.latitude,
                view,
                token: serviceLayer.service?.token,
                tolerance: identifyTolerance,
                fallbackLayerName: 'Unknown Map Service',
              });

              if (identifyResult) {
                if (!attributesBySource[identifyResult.sourceTitle]) {
                  attributesBySource[identifyResult.sourceTitle] = [];
                }
                attributesBySource[identifyResult.sourceTitle].push(
                  identifyResult.attributes,
                );
              }
            }
          }
        }

        dispatch(
          showMapClickInfo(
            formatMapClickInfo(
              {
                mapPoint: {
                  latitude: selectedResult.latitude,
                  longitude: selectedResult.longitude,
                },
              },
              {
                layerName: selectedResult.data?.display_name,
                layerId: String(selectedResult.data.id),
                attributes: attributesBySource,
                isLoading: false, // Remove loading flag
              },
            ),
          ),
        );
      },
      [
        dispatch,
        activeServiceLayers,
        view,
        isBasemapServiceOn,
        basemapService,
        gisBasemapSublayers,
      ],
    );

    const handleMapClick = useCallback(
      async (event: MapClickEvent) => {
        if (mapRef) mapRef.style.cursor = 'wait';

        try {
          const results: LayerSelectionResult[] = [];

          // Add active GIS services to the list of suggested results
          // this includes both basemap and supplemental service layers
          activeGISServices.forEach(service => {
            results.push({
              layerName: service.display_name,
              layerId: String(service.id),
              feature: { attributes: {} },
              data: service,
              layerType: service.attributes.gis_service_category || '',
              mapPoint: event?.mapPoint,
              latitude: event.mapPoint.latitude,
              longitude: event.mapPoint.longitude,
            });
          });

          // If result list has only one item, select it immediately
          if (results.length === 1) {
            handleLayerSelect({
              layerName: results[0].layerName,
              layerId: results[0].layerId,
              feature: { attributes: results[0].feature.attributes || {} },
              latitude: event.mapPoint.latitude,
              longitude: event.mapPoint.longitude,
              data: results[0].data,
              layerType: results[0].layerType,
              mapPoint: event?.mapPoint,
            });
          } else if (results.length > 1) {
            // If result list has multiple items, show the layer selection menu
            setLayerSelectionMenu({
              results: results.map(result => ({
                layerName: result.layerName,
                layerId: result.layerId,
                feature: { attributes: result.feature.attributes || {} },
                latitude: event.mapPoint.latitude,
                longitude: event.mapPoint.longitude,
                data: result.data,
                layerType: result.layerType,
                mapPoint: event?.mapPoint,
              })),
              screenPoint: { x: event.x, y: event.y },
            });
          }
        } catch {
          // fail silently
        } finally {
          if (mapRef) mapRef.style.cursor = 'auto';
        }
      },
      [
        featureLayers,
        mapRef,
        view,
        mapImageLayers,
        activeGISServices,
        handleLayerSelect,
      ],
    );

    useEffect(() => {
      if (!hasActiveLayers || !view) return;

      const mapClickHandler = view.on('click', (event: MapClickEvent) => {
        if (event.button === 2) {
          // right click
          event.stopPropagation();
          event.preventDefault();
          if (mapClickInfo) {
            dispatch(hideMapClickInfo());
          }
          handleMapClick(event);
        } else {
          setLayerSelectionMenu(null);
          dispatch(hideMapClickInfo());
        }
      });

      return () => mapClickHandler?.remove();
    }, [hasActiveLayers, view, handleMapClick, dispatch, mapClickInfo]);

    useEffect(() => {
      if (!hasActiveLayers && layerSelectionMenu) {
        handlePopupClose();
      }
    }, [hasActiveLayers, layerSelectionMenu, handlePopupClose]);

    return layerSelectionMenu ? (
      <LayerSelectionMenu
        results={layerSelectionMenu.results}
        screenPoint={layerSelectionMenu.screenPoint}
        onLayerSelect={handleLayerSelect}
      />
    ) : null;
  },
);

export default ClickToIdentifyGL;
